import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { get } from 'lodash';
import { useFormikContext } from 'formik';
// Import Types
import { ButtonBuilderFormFields } from '../types';
// Import Components
import { Button, ExtendedInput } from '@geneui/components';
// Import Constants
import l from 'constants/localizationLabels';
import { BUTTON_ROWS_FORM_KEY } from '../constants';
// Import Styles
import styles from './buttonRowsBuilder.module.scss';
// Import Hooks
import { useSubmitSubscription } from 'context';

interface Props {
    rowIndex: number;
    buttonIndex: number;
    handleRemove: (index: number) => () => void;
    onFocus: () => void;
}

const ButtonDetailsForm: FC<Props> = ({ rowIndex, buttonIndex, handleRemove, onFocus }) => {
    const { t } = useTranslation();
    const { triggeredSubmissionsCount } = useSubmitSubscription();
    const isPageSubmitted = !!triggeredSubmissionsCount;

    const { values, errors, touched, handleChange, handleBlur } = useFormikContext<ButtonBuilderFormFields>();
    const textFieldName = `${BUTTON_ROWS_FORM_KEY}[${rowIndex}][${buttonIndex}].text`;
    const urlFieldName = `${BUTTON_ROWS_FORM_KEY}[${rowIndex}][${buttonIndex}].url`;
    const textError = get(errors, textFieldName);
    const textTouched = get(touched, textFieldName);
    const textValue = get(values, textFieldName);
    const urlError = get(errors, urlFieldName);
    const urlTouched = get(touched, urlFieldName);
    const urlValue = get(values, urlFieldName);
    const shouldShowTextError = isPageSubmitted || textTouched;
    const shouldShowUrlError = isPageSubmitted || urlTouched;

    return (
        <div className={classNames(styles.keyValueFieldsContainer, { [styles.nested]: true })}>
            <div className={styles.keyValueFields}>
                <ExtendedInput
                    type="text"
                    name={textFieldName}
                    label={t(l.ButtonText)}
                    title={t(l.ButtonText)}
                    placeholder={t(l.ButtonText)}
                    labelAppearance="swap"
                    value={textValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorText={textError}
                    isValid={shouldShowTextError ? !textError : undefined}
                    onFocus={onFocus}
                />
                <ExtendedInput
                    type="text"
                    name={urlFieldName}
                    label={t(l.ButtonUrl)}
                    title={t(l.ButtonUrl)}
                    placeholder={t(l.ButtonUrl)}
                    labelAppearance="swap"
                    value={urlValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorText={urlError}
                    isValid={shouldShowUrlError ? !urlError : undefined}
                    onFocus={onFocus}
                />
            </div>

            <Button
                className={styles.removeButton}
                flexibility="default"
                appearance="minimal"
                icon="bc-icon-trash"
                onClick={handleRemove(buttonIndex)}
            />
        </div>
    );
};

export default memo(ButtonDetailsForm);
